/*
  BC Metabolisme Score — quiz behaviour
  ---------------------------------------------------------------------------
  Plain JavaScript, no libraries, no build step.
  Parts, in order:
    1. Setup and helpers
    2. Reading the editor content (questions and products)
    3. Checks on load (warnings in the console)
    4. Saving progress (sessionStorage, this tab only)
    5. Navigation between screens
    6. Answers and validation
    7. Scoring
    8. Recommendation
    9. Result page
   10. Result link (for the email)
   11. Klaviyo
   12. Tracking
   13. Cart
   14. Start

  Debugging: add ?debug=1 to the page URL. The quiz then logs what it does.
*/
(function () {
  'use strict';

  // The whole quiz starts in boot(). In the theme editor the section is re-rendered after every change;
  // boot() then runs again on the new markup (see the end of this file).
  function boot() {

  /* ─────────────────────────── 1. Setup and helpers ─────────────────────────── */

  var root = document.getElementById('ms-quiz');
  if (!root) return;

  var cfg = JSON.parse(root.querySelector('[data-ms-config]').textContent);
  var EDITOR = cfg.editor === true; // Shopify theme editor: nothing is saved, tracked or sent to Klaviyo
  var DEBUG = /[?&]debug=1/.test(window.location.search) || window.MS_QUIZ_DEBUG === true;
  var STORAGE_KEY = 'ms-quiz-v1';
  var SUBS = ['sugar', 'fat', 'rest', 'basis']; // fixed order, also used to break ties

  function log() {
    if (!DEBUG) return;
    var args = Array.prototype.slice.call(arguments);
    console.log.apply(console, ['[MS-Quiz]'].concat(args));
  }
  function warn(msg) { console.warn('[MS-Quiz] ' + msg); }

  function $(sel, el) { return (el || root).querySelector(sel); }
  function $$(sel, el) { return Array.prototype.slice.call((el || root).querySelectorAll(sel)); }

  // Fills {placeholders} in a text. Unknown placeholders stay empty.
  function fill(text, vars) {
    return String(text || '').replace(/\{(\w+)\}/g, function (_, name) {
      return vars[name] !== undefined && vars[name] !== null ? vars[name] : '';
    });
  }

  function num(v, fallback) {
    var n = parseFloat(v);
    return isNaN(n) ? fallback : n;
  }

  function lines(text) {
    return String(text || '').split(/\r?\n/).map(function (l) { return l.trim(); }).filter(Boolean);
  }

  // Dates: always 'YYYY-MM-DD' strings in state, shown in the page language.
  function toISO(d) {
    var m = String(d.getMonth() + 1).padStart(2, '0');
    var day = String(d.getDate()).padStart(2, '0');
    return d.getFullYear() + '-' + m + '-' + day;
  }
  function fromISO(s) {
    var p = String(s || '').split('-');
    if (p.length !== 3) return null;
    return new Date(+p[0], +p[1] - 1, +p[2]);
  }
  function addDays(d, n) { var c = new Date(d); c.setDate(c.getDate() + n); return c; }
  function daysBetween(a, b) { return Math.round((b - a) / 86400000); }
  var locale = cfg.language === 'fr' ? 'fr-BE' : cfg.language === 'en' ? 'en-GB' : 'nl-BE';
  function formatDate(d) { return d.toLocaleDateString(locale, { day: 'numeric', month: 'long' }); }
  function formatShort(d) { return d.toLocaleDateString(locale, { day: 'numeric', month: 'short' }).replace('.', ''); }

  /* ─────────────── 2. Reading the editor content (questions and products) ─────────────── */

  var screens = $$('.ms-screen').map(function (el) {
    return {
      id: el.getAttribute('data-screen-id'),
      type: el.getAttribute('data-type'),
      showIf: el.getAttribute('data-show-if') || 'always',
      el: el
    };
  });

  function keyList(text) {
    return String(text || '').split(',').map(function (k) { return k.trim().toLowerCase(); }).filter(Boolean);
  }

  var questions = {};      // by key
  var questionList = [];   // in quiz order
  $$('[data-ms-question]').forEach(function (script) {
    var q = JSON.parse(script.textContent);
    q.el = script.closest('.ms-screen');
    q.weight = num(q.weight, 1);
    q.productPoints = num(q.productPoints, 1);
    // Keep option slots in place (slot index = answer value), but mark empty ones.
    q.options = q.options.map(function (o, i) {
      return {
        index: i, label: o.label, used: !!o.label,
        points: o.points === null ? null : num(o.points, null),
        summary: o.summary, products: keyList(o.products), route: o.route || 'none'
      };
    });
    questions[q.key] = q;
    questionList.push(q);
  });

  function byRole(role) {
    for (var i = 0; i < questionList.length; i++) if (questionList[i].role === role) return questionList[i];
    return null;
  }

  // Product blocks, by key, in editor order.
  var products = {};
  var productOrder = [];
  $$('[data-ms-product-card]').forEach(function (el) {
    var key = el.getAttribute('data-ms-product-card');
    if (!key || products[key]) return;
    products[key] = {
      key: key,
      canMain: el.getAttribute('data-can-main') === 'true',
      isPack: el.getAttribute('data-is-pack') === 'true',
      canAddon: el.getAttribute('data-can-addon') === 'true',
      contents: keyList(el.getAttribute('data-contents')),
      title: el.getAttribute('data-title'),
      handle: el.getAttribute('data-handle'),
      actions: el.getAttribute('data-actions'),
      email: readEmailData(el),
      el: el,
      addonEl: root.querySelector('[data-ms-addon-card="' + key + '"]'),
      looseEl: root.querySelector('[data-ms-loose-card="' + key + '"]')
    };
    productOrder.push(key);
  });
  // Price, photo, link, rating and benefit lines for the Klaviyo event (see part 11).
  function readEmailData(el) {
    var script = el.querySelector('[data-ms-email]');
    try { return script ? JSON.parse(script.textContent) : {}; } catch (e) { warn('Email data of "' + el.getAttribute('data-ms-product-card') + '" could not be read.'); return {}; }
  }
  // Only add-on products (like Instant Energizer) never pull a pack to the top.
  function addonOnly(key) { var p = products[key]; return p && !p.canMain && p.canAddon; }

  /* ─────────────────────────── 3. Checks on load ─────────────────────────── */

  function runChecks() {
    var problems = [];
    var seen = {};
    questionList.forEach(function (q) {
      if (seen[q.key]) problems.push('Two questions share the key "' + q.key + '".');
      seen[q.key] = true;
      if (q.type === 'single' || q.type === 'multi') {
        var used = q.options.filter(function (o) { return o.used; });
        if (used.length < 2) problems.push('Question "' + q.key + '" has fewer than two options.');
        used.forEach(function (o) {
          if (q.subscore !== 'none' && o.points === null) problems.push('Question "' + q.key + '", option ' + (o.index + 1) + ' has no points.');
          o.products.forEach(function (k) {
            if (!products[k]) problems.push('Question "' + q.key + '", option ' + (o.index + 1) + ' links to product key "' + k + '", but no Product block has that key.');
          });
          if (o.route !== 'none' && !o.products.length) problems.push('Question "' + q.key + '", option ' + (o.index + 1) + ' has a route but no products.');
        });
      }
    });
    ['gate', 'goal', 'current_weight'].forEach(function (role) {
      if (!byRole(role)) problems.push('No question has the role "' + role + '".');
    });
    if (!productOrder.length) problems.push('There are no Product blocks.');
    productOrder.forEach(function (k) {
      var p = products[k];
      p.contents.forEach(function (c) { if (!products[c]) problems.push('Product "' + k + '" lists "' + c + '" as contents, but no Product block has that key.'); });
      var found = p.el.getAttribute('data-plan-found');
      var names = lines((p.el.getAttribute('data-plan-names') || '').replace(/\|/g, '\n'))
        .filter(function (n, i, all) { return all.indexOf(n) === i; });
      var product = p.el.getAttribute('data-product');
      if (!product) { problems.push('Product "' + k + '": no Shopify product picked.'); return; }
      if (found) {
        log('Subscription for "' + k + '" (' + product + '): plan "' + found + '".');
      } else if (names.length) {
        problems.push('Product "' + k + '" (' + product + '): no subscription plan matches "' + (p.el.getAttribute('data-plan-search') || '') +
          '". Plans on this product: ' + names.map(function (n) { return '"' + n + '"'; }).join(', ') +
          '. Put part of one of these names in "Subscription plan name contains", or leave it empty.');
      } else {
        log('Product "' + k + '" (' + product + ') has no subscription plan: shown as a one-time purchase.');
      }
    });
    if (!$('[data-ms-email-form]')) problems.push('There is no email gate block.');
    problems.forEach(warn);
    log('Checks done.', problems.length ? problems.length + ' warning(s).' : 'No problems.');
  }

  /* ─────────────── 4. Saving progress (sessionStorage, this tab only) ─────────────── */

  var state = { answers: {}, visited: ['start'], result: null };

  function save() {
    if (EDITOR) return;
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch (e) { /* private mode: fine */ }
  }
  function load() {
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        var saved = JSON.parse(raw);
        if (saved && saved.answers) state = saved;
      }
    } catch (e) { /* ignore */ }
  }

  /* ─────────────────────────── 5. Navigation ─────────────────────────── */

  var current = null;

  // The first chosen answer on the safety question (in editor order) that has a route decides.
  // Safety routes. Any question can carry them (pregnancy sits in Q2, medication in Q3).
  // The first chosen answer with a route wins: questions in quiz order, answers in editor order.
  function gateChoice() {
    for (var qi = 0; qi < questionList.length; qi++) {
      var q = questionList[qi];
      var a = state.answers[q.key];
      var idx = typeof a === 'number' ? [a] : (Array.isArray(a) ? a.slice().sort(function (x, y) { return x - y; }) : []);
      for (var i = 0; i < idx.length; i++) {
        var o = q.options[idx[i]];
        if (o && o.route !== 'none') return o;
      }
    }
    return null;
  }
  // "Medical" answers (consult or only-these-products) show the "Fijn dat je dit deelt" screen.
  function gatePositive() {
    var g = gateChoice();
    return !!g && (g.route === 'consult' || g.route === 'only');
  }
  function lastUsedIndex(q) {
    var idx = -1;
    q.options.forEach(function (o) { if (o.used) idx = o.index; });
    return idx;
  }

  function isActive(screen) {
    if (screen.showIf === 'gate_positive') return gatePositive();
    return true;
  }
  function activeScreens() { return screens.filter(isActive); }
  function findScreen(id) {
    for (var i = 0; i < screens.length; i++) if (screens[i].id === id) return screens[i];
    return null;
  }

  function show(id, options) {
    options = options || {};
    var screen = findScreen(id);
    if (!screen) screen = screens[0];

    screens.forEach(function (s) { s.el.hidden = s !== screen; });
    current = screen;

    // Site header is hidden during the quiz; the footer comes back on the result.
    document.body.classList.toggle('ms-quiz-active', screen.type !== 'result');
    document.body.classList.toggle('ms-quiz-result', screen.type === 'result');

    var isQuestion = screen.type === 'question';
    $('[data-ms-topbar]').hidden = screen.type === 'welcome' || screen.type === 'result';
    $('[data-ms-progress]').hidden = !isQuestion;
    if (isQuestion) updateProgress(screen);

    if (state.visited.indexOf(screen.id) === -1) state.visited.push(screen.id);
    save();

    window.scrollTo(0, 0);
    var focusEl = $('[data-ms-focus]', screen.el);
    if (focusEl && options.focus !== false) focusEl.focus({ preventScroll: true });

    if (screen.type === 'question') restoreAnswer(questionFor(screen));
    if (screen.type === 'analysing') runAnalysing(screen);
    if (screen.type === 'result') renderResult();

    // Update the address bar last. Some browsers (Safari on local files) refuse this;
    // the quiz must keep working then, only the back button falls back to the arrow.
    if (options.push !== false) setUrl('push', screen.id);

    track('quiz_step', { step_id: screen.id, step_index: activeScreens().indexOf(screen) + 1 });
    log('Screen:', screen.id);
  }

  function setUrl(mode, id, url) {
    try {
      history[mode === 'push' ? 'pushState' : 'replaceState']({ ms: id }, '', url || '#' + id);
    } catch (e) {
      log('Address bar not updated (' + e.name + '). The quiz keeps working.');
    }
  }

  // Back arrow: previous active screen. Uses the browser history when it has our entries.
  function goBack() {
    if (history.state && history.state.ms && history.state.ms !== 'start' && window.location.hash === '#' + current.id) {
      history.back();
      return;
    }
    var list = activeScreens();
    var prev = list[list.indexOf(current) - 1];
    if (prev) show(prev.id, { push: false });
  }

  function next() {
    var list = activeScreens();
    var i = list.indexOf(current);
    var target = list[i + 1];
    if (target) show(target.id);
  }

  function updateProgress(screen) {
    var counted = questionList.filter(function (q) { return findScreen(q.key).showIf === 'always'; });
    var q = questionFor(screen);
    var n = counted.indexOf(q) + 1;
    if (n === 0) {
      // Follow-up questions (like the date) show the number of the question before them.
      var before = questionList.slice(0, questionList.indexOf(q)).filter(function (x) { return counted.indexOf(x) !== -1; });
      n = before.length;
    }
    var total = cfg.questionTotal || counted.length;
    $('[data-ms-progress-fill]').style.width = Math.round((n / total) * 100) + '%';
    $('[data-ms-progress-block]').textContent = screen.el.getAttribute('data-block-label') || '';
    var countEl = $('[data-ms-progress-count]');
    countEl.textContent = fill(countEl.getAttribute('data-tpl'), { n: n, totaal: total });
  }

  // Back button and browser back/forward use the #hash of each screen.
  window.addEventListener('popstate', function () {
    if (!root.isConnected) return; // an older copy of the quiz, replaced by the theme editor
    var id = window.location.hash.replace('#', '');
    var screen = findScreen(id);
    // Only allow screens the visitor has already reached, and never go back into the quiz from a result link.
    if (!screen || state.visited.indexOf(id) === -1 || (state.result && screen.type !== 'result' && state.fromLink)) {
      id = state.result ? 'resultaat' : 'start';
    }
    show(id, { push: false });
  });

  /* ─────────────────────────── 6. Answers and validation ─────────────────────────── */

  function questionFor(screen) { return questions[screen.el.getAttribute('data-q-key')]; }

  function restoreAnswer(q) {
    var a = state.answers[q.key];
    var btn = $('[data-ms-next]', q.el);
    if (q.type === 'single') {
      $$('input', q.el).forEach(function (i) { i.checked = +i.value === a; });
    } else if (q.type === 'multi') {
      $$('input', q.el).forEach(function (i) { i.checked = Array.isArray(a) && a.indexOf(+i.value) !== -1; });
    } else if (a !== undefined) {
      $('input', q.el).value = a;
    }
    btn.disabled = !hasAnswer(q);
    setError(q.el, '');
  }

  function hasAnswer(q) {
    var a = state.answers[q.key];
    if (q.type === 'multi') return Array.isArray(a) && a.length > 0;
    if (q.type === 'single') return typeof a === 'number';
    return a !== undefined && a !== '';
  }

  function setError(scope, msg) {
    var el = $('[data-ms-error]', scope);
    if (el) { el.textContent = msg; el.classList.toggle('ms-error--visible', !!msg); }
  }

  function onInput(q, input) {
    if (q.type === 'single') {
      state.answers[q.key] = +input.value;
    } else if (q.type === 'multi') {
      var noneIndex = q.role === 'gate' ? lastUsedIndex(q) : -1;
      var boxes = $$('input', q.el);
      // "None of these" clears the others, and the others clear "none of these".
      if (+input.value === noneIndex && input.checked) {
        boxes.forEach(function (b) { if (+b.value !== noneIndex) b.checked = false; });
      } else if (input.checked) {
        boxes.forEach(function (b) { if (+b.value === noneIndex) b.checked = false; });
      }
      state.answers[q.key] = boxes.filter(function (b) { return b.checked; }).map(function (b) { return +b.value; });
    } else {
      state.answers[q.key] = input.value;
    }
    setError(q.el, '');
    $('[data-ms-next]', q.el).disabled = !hasAnswer(q);
    save();
  }

  function validate(q) {
    var a = state.answers[q.key];
    if (q.type === 'number') {
      var n = num(String(a).replace(',', '.'), NaN);
      var min = num(q.min, 0), max = num(q.max, 999);
      if (isNaN(n) || n < min || n > max) {
        setError(q.el, fill(cfg.errorNumber, { min: min, max: max }));
        return false;
      }
      state.answers[q.key] = n;
    }
    if (q.type === 'date') {
      var d = fromISO(a);
      if (!d || d < fromISO(toISO(new Date()))) { setError(q.el, cfg.errorDate); return false; }
    }
    return true;
  }

  root.addEventListener('change', function (e) {
    var screenEl = e.target.closest('.ms-question');
    if (!screenEl) return;
    onInput(questions[screenEl.getAttribute('data-q-key')], e.target);
  });
  // Price block: one-time purchase or monthly subscription. The choice sets the plan on the button.
  root.addEventListener('change', function (e) {
    if (!e.target.classList || !e.target.classList.contains('ms-buy__radio')) return;
    var buy = e.target.closest('[data-ms-buy]');
    $$('.ms-buy__opt', buy).forEach(function (opt) { opt.classList.toggle('is-selected', opt.contains(e.target)); });
    var btn = $('[data-ms-main-add]', buy);
    if (btn) btn.setAttribute('data-plan', e.target.value || '');
    log('Purchase type:', e.target.value ? 'subscription (plan ' + e.target.value + ')' : 'one-time');
  });
  root.addEventListener('input', function (e) {
    var screenEl = e.target.closest('.ms-question');
    if (!screenEl || e.target.type === 'radio' || e.target.type === 'checkbox') return;
    onInput(questions[screenEl.getAttribute('data-q-key')], e.target);
  });

  root.addEventListener('click', function (e) {
    var nextBtn = e.target.closest('[data-ms-next]');
    if (nextBtn) {
      if (nextBtn.hasAttribute('data-ms-start')) {
        state = { answers: {}, visited: ['start'], result: null };
        track('quiz_start', {});
      }
      if (current.type === 'question') {
        var q = questionFor(current);
        if (!validate(q)) return;
        var gc = gateChoice();
        if (gc && (q.role === 'gate' || q.options.some(function (o) { return o.route !== 'none'; }))) track('quiz_gate', { route: gc.route });
      }
      next();
      return;
    }
    if (e.target.closest('[data-ms-back]')) { goBack(); return; }

    var scrollBtn = e.target.closest('[data-ms-scroll-to]');
    if (scrollBtn) {
      var target = document.getElementById('ms-' + scrollBtn.getAttribute('data-ms-scroll-to'));
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    var addBtn = e.target.closest('[data-ms-add]');
    if (addBtn) { addToCart(addBtn); return; }
    if (e.target.closest('[data-ms-repeat-cta]')) {
      var visible = $('.ms-outcome:not([hidden]) [data-ms-main-add]');
      if (visible) addToCart(visible);
    }
  });

  // Enter on a number or date field moves on, like pressing the button.
  root.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' || !e.target.classList.contains('ms-field__input')) return;
    if (e.target.type === 'email') return;
    e.preventDefault();
    var btn = $('[data-ms-next]', current.el);
    if (btn && !btn.disabled) btn.click();
  });

  // Analysing screens tick off one line at a time, then enable the button.
  function runAnalysing(screen) {
    var items = $$('.ms-ticks__item', screen.el);
    var fillBar = $('[data-ms-analysing-fill]', screen.el);
    var btn = $('[data-ms-next]', screen.el);
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var step = reduce ? 0 : 800;
    items.forEach(function (li) { li.classList.remove('is-done'); });
    btn.disabled = true;
    fillBar.style.width = '0%';
    items.forEach(function (li, i) {
      setTimeout(function () {
        li.classList.add('is-done');
        fillBar.style.width = Math.round(((i + 1) / items.length) * 100) + '%';
        if (i === items.length - 1) btn.disabled = false;
      }, step * (i + 1));
    });
    if (!items.length) btn.disabled = false;
  }

  /* ─────────────────────────── 7. Scoring ─────────────────────────── */

  // Points of an answer. For "choose several", the lowest-scoring chosen answer counts.
  function answerPoints(q, a) {
    if (q.type === 'single') {
      var o = typeof a === 'number' ? q.options[a] : null;
      return o && o.points !== null ? o.points : null;
    }
    if (q.type === 'multi' && Array.isArray(a)) {
      var pts = a.map(function (i) { return q.options[i] && q.options[i].points; })
        .filter(function (p) { return p !== null && p !== undefined; });
      return pts.length ? Math.min.apply(null, pts) : null;
    }
    return null;
  }

  function computeScores() {
    var groups = { sugar: [], fat: [], rest: [], basis: [] };
    questionList.forEach(function (q) {
      if (!groups[q.subscore]) return;
      var p = answerPoints(q, state.answers[q.key]);
      if (p === null) return;
      groups[q.subscore].push({ points: p, weight: q.weight });
    });
    var subs = {};
    SUBS.forEach(function (k) {
      var list = groups[k], total = 0, weights = 0;
      list.forEach(function (x) { total += x.points * x.weight; weights += x.weight; });
      subs[k] = weights ? Math.round(total / weights) : null;
    });
    var present = SUBS.filter(function (k) { return subs[k] !== null; });
    var sum = present.reduce(function (acc, k) { return acc + subs[k]; }, 0);
    var score = present.length ? Math.round(sum / present.length) : 0;
    log('Scores:', { total: score, sub: subs });
    return { score: score, subs: subs };
  }

  function rankSubs(subs) {
    return SUBS.filter(function (k) { return subs[k] !== null; })
      .sort(function (a, b) { return subs[a] - subs[b] || SUBS.indexOf(a) - SUBS.indexOf(b); });
  }

  function band(score) {
    for (var i = 0; i < cfg.bands.length; i++) if (score <= num(cfg.bands[i].max, 100)) return i;
    return cfg.bands.length - 1;
  }

  /* ─────────────────────────── 8. Recommendation ─────────────────────────── */
  // 1. Safety routes go first (consult / only these products / best of these products).
  // 2. Otherwise every chosen answer gives points to the products it links to.
  //    The main routine is always a PACK: the pack with the most points (its own points plus
  //    those of its contents, once at least two of them scored). Ties go to the smaller pack.
  //    Only packs with "Can be the main routine" on compete (Metabolisme: the weight-loss packs).
  // 3. A second card: by default the extra suggestion (best-scoring product outside the main routine);
  //    setting "Second card shows" can switch it back to one single product for the biggest lever.

  function productPoints() {
    var pts = {};
    questionList.forEach(function (q) {
      if (q.role === 'gate') return; // the safety question works through its routes, not through points
      var a = state.answers[q.key];
      var chosen = q.type === 'single' && typeof a === 'number' ? [a] : (q.type === 'multi' && Array.isArray(a) ? a : []);
      chosen.forEach(function (i) {
        var o = q.options[i];
        if (!o || o.route !== 'none') return; // an answer with a route works through that route
        o.products.forEach(function (k) { pts[k] = (pts[k] || 0) + q.productPoints; });
      });
    });
    return pts;
  }

  // How a pack is chosen: problems and goals decide.
  // 1. A pack scores the answers that point to the pack itself (problem answers and the goal).
  // 2. On a tie, the most specific pack wins: the one with the fewest products in it.
  // 3. Still a tie: the pack whose products got the most points from the other answers.
  function productScore(key, pts) { return pts[key] || 0; }
  function insidePoints(key, pts) {
    var p = products[key];
    if (!p || !p.contents.length) return 0;
    return p.contents.filter(function (c) { return !addonOnly(c); })
      .reduce(function (acc, c) { return acc + (pts[c] || 0); }, 0);
  }

  function bestOf(keys, pts) {
    var best = null;
    keys.forEach(function (k) {
      if (!products[k]) return;
      if (best === null) { best = k; return; }
      var a = productScore(k, pts), b = productScore(best, pts);
      var ca = products[k].contents.length, cb = products[best].contents.length;
      if (a > b || (a === b && ca < cb) || (a === b && ca === cb && insidePoints(k, pts) > insidePoints(best, pts))) best = k;
    });
    return { key: best, score: best ? productScore(best, pts) : -1 };
  }

  // The single product for the biggest lever; if that is already the main product, the next lever.
  // Did the visitor tick the "man" answer (section setting "Answer that means the visitor is a man")?
  function isMan() {
    var label = String(cfg.menAnswer || '').trim().toLowerCase();
    if (!label) return false;
    return questionList.some(function (q) {
      var a = state.answers[q.key];
      var idx = typeof a === 'number' ? [a] : (Array.isArray(a) ? a : []);
      return idx.some(function (i) { return q.options[i] && String(q.options[i].label).trim().toLowerCase() === label; });
    });
  }

  function singleFor(ranked, main) {
    var map = cfg.singleMap || {};
    var man = isMan();
    for (var i = 0; i < ranked.length; i++) {
      var k = String((man && ranked[i] === 'basis' && cfg.singleBasisMen) ? cfg.singleBasisMen : (map[ranked[i]] || '')).trim().toLowerCase();
      if (k && products[k] && k !== main) return { key: k, lever: ranked[i] };
      if (k && !products[k]) warn('Single product for "' + ranked[i] + '" is "' + k + '", but no Product block has that key.');
    }
    return { key: null, lever: null };
  }

  // The extra suggestion: the best-scoring product outside the main routine (on as "extra", off as "main"),
  // never the main pack or a product already inside it. Nothing scored: no extra card.
  function extraFor(pts, main) {
    var inside = main && products[main] ? products[main].contents : [];
    var keys = productOrder.filter(function (k) {
      var p = products[k];
      return p.canAddon && !p.canMain && k !== main && inside.indexOf(k) === -1 && (pts[k] || 0) > 0;
    });
    return { key: bestOf(keys, pts).key, lever: null };
  }

  // Which texts the second card uses: the medication route always shows "one single product".
  function secondCardIsExtra(route) {
    return route !== 'only' && cfg.secondCard !== 'single';
  }

  function recommend(subs) {
    var pts = productPoints();
    var gate = gateChoice();
    var route = gate ? gate.route : 'none';
    var list = gate ? gate.products.filter(function (k) { return products[k]; }) : [];
    var ranked = rankSubs(subs);
    var main = null, single = { key: null, lever: null };

    if (route === 'consult') {
      main = list[0] || null;                         // e.g. Smart Vitamins, no single product
    } else if (route === 'only') {
      // Medical: only the listed products. The main routine is a pack from that list when there is one.
      var safePacks = list.filter(function (k) { return products[k].isPack; });
      main = (safePacks.length ? bestOf(safePacks, pts).key : bestOf(list, pts).key) || list[0] || null;
      var rest = list.filter(function (k) { return k !== main; })
        .sort(function (a, b) { return (pts[b] || 0) - (pts[a] || 0) || list.indexOf(a) - list.indexOf(b); });
      single = { key: rest[0] || null, lever: null };
    } else {
      var packs = route === 'pick' ? list : productOrder.filter(function (k) { return products[k].canMain && (products[k].isPack || products[k].contents.length); });
      if (!packs.length) packs = productOrder.filter(function (k) { return products[k].canMain; });
      var best = bestOf(packs, pts);
      main = best.score > 0 ? best.key : (packs[0] || null);
      single = secondCardIsExtra(route) ? extraFor(pts, main) : singleFor(ranked, main);
    }
    log('Product points:', pts);
    log('Recommendation: route "' + route + '", main "' + main + '", ' + (secondCardIsExtra(route) ? 'extra' : 'single') + ' "' + single.key + '"' +
      (single.lever ? ' (biggest lever: ' + single.lever + ')' : '') + (gate ? ' (safety answer: "' + gate.label + '")' : ''));
    return { main: main, single: single.key, singleLever: single.lever, route: route, gateReason: gate ? gate.summary : '' };
  }

  /* ─────────────────────────── 9. Result page ─────────────────────────── */

  function chosenSummaries(role, answers) {
    var q = byRole(role);
    var a = q && answers[q.key];
    var idx = typeof a === 'number' ? [a] : (Array.isArray(a) ? a : []);
    return idx.map(function (i) { return q.options[i] && q.options[i].summary; }).filter(Boolean);
  }

  function chosenLabels(role, answers) {
    var q = byRole(role);
    var a = q && answers[q.key];
    var idx = typeof a === 'number' ? [a] : (Array.isArray(a) ? a : []);
    return idx.map(function (i) { return q.options[i] && q.options[i].label; }).filter(Boolean);
  }

  // Goal in kg, read from the answer text: "5 tot 10 kg" → 7.5, "Meer dan 10 kg" → 10.
  function goalKg() {
    var q = byRole('goal');
    var a = q && state.answers[q.key];
    var o = typeof a === 'number' ? q.options[a] : null;
    if (!o) return null;
    var nums = (o.label.match(/\d+(?:[.,]\d+)?/g) || []).map(function (n) { return parseFloat(n.replace(',', '.')); });
    if (!nums.length) return null;
    return nums.length > 1 ? (nums[0] + nums[1]) / 2 : nums[0];
  }

  // Target weight for the projection text. Never below the healthy-BMI floor.
  function targetWeight() {
    var kg = goalKg();
    var wq = byRole('current_weight'), hq = byRole('height');
    var w = wq ? num(String(state.answers[wq.key]).replace(',', '.'), NaN) : NaN;
    if (kg === null || isNaN(w)) return null;
    var target = Math.round((w - kg) * 2) / 2;
    var h = hq ? num(String(state.answers[hq.key]).replace(',', '.'), NaN) / 100 : NaN;
    if (!isNaN(h) && h > 0 && target / (h * h) < num(cfg.bmiFloor, 18.5)) {
      log('Target weight left out: it would be below the BMI floor.');
      return null;
    }
    return target;
  }

  // Everything the result page shows is computed once, at the email gate, and stored.
  function buildResult() {
    var calc = computeScores();
    var rec = recommend(calc.subs);
    return {
      score: calc.score,
      subs: calc.subs,
      main: rec.main,
      single: rec.single,
      singleLever: rec.singleLever,
      route: rec.route,
      gateReason: rec.gateReason,
      start: toISO(new Date()),
      targetWeight: targetWeight(),
      goalSummary: chosenSummaries('goal', state.answers)[0] || '',
      lifestage: chosenLabels('lifestage', state.answers).join(', '),
      allAnswers: JSON.parse(JSON.stringify(state.answers)), // this tab only, never in the link
      answers: safeAnswers()
    };
  }

  // Answers kept in the result link: only scored single-choice habit answers and the goal.
  // Never the safety question, life stage, age, height or weight.
  function safeAnswers() {
    var out = {};
    questionList.forEach(function (q) {
      if (q.type !== 'single') return;
      if (q.subscore === 'none' && q.role !== 'goal') return;
      if (typeof state.answers[q.key] === 'number') out[q.key] = state.answers[q.key];
    });
    return out;
  }

  function levers(result, ranked) {
    var answers = result.allAnswers || result.answers;
    return ranked.slice(0, 2).map(function (sub) {
      var picks = [];
      questionList.forEach(function (q) {
        if (q.subscore !== sub) return;
        var a = answers[q.key];
        var idx = typeof a === 'number' ? [a] : (Array.isArray(a) ? a : []);
        idx.forEach(function (i) { var o = q.options[i]; if (o) picks.push(o); });
      });
      var detail = picks
        .filter(function (o) { return o.summary && o.points !== null && o.points < num(cfg.leverThreshold, 60); })
        .sort(function (a, b) { return a.points - b.points; })
        .slice(0, 2)
        .map(function (o) { return o.summary; });
      return { name: cfg.subscoreNames[sub], detail: detail.join(', ') };
    });
  }

  function setRow(name, value, detail) {
    var row = $('[data-ms-row="' + name + '"]');
    if (!row) return;
    row.hidden = !value;
    $('[data-ms-value]', row).textContent = value || '';
    $('[data-ms-detail]', row).textContent = detail || '';
  }

  function renderResult() {
    var r = state.result;
    if (!r) { show('start'); return; }
    var ranked = rankSubs(r.subs);
    var b = band(r.score);

    // R1. Score
    $('[data-ms-score]').textContent = r.score;
    $('[data-ms-gauge-arc]').setAttribute('stroke-dasharray', r.score + ' 100');
    $('[data-ms-band]').textContent = cfg.bands[b].name;
    var bandText = $('[data-ms-band-text]');
    if (bandText) { bandText.textContent = (cfg.bandTexts || [])[b] || ''; bandText.hidden = !bandText.textContent; }
    var scale = $('[data-ms-bandscale]');
    scale.innerHTML = '';
    var from = 0;
    cfg.bands.forEach(function (band_, i) {
      var li = document.createElement('li');
      if (i === b) li.className = 'is-current';
      li.innerHTML = '<span></span><strong></strong><small></small>';
      li.querySelector('strong').textContent = band_.name;
      li.querySelector('small').textContent = from + '–' + band_.max;
      scale.appendChild(li);
      from = num(band_.max, 100) + 1;
    });

    // R2. Assessment
    var bars = $('[data-ms-bars]');
    bars.innerHTML = '';
    SUBS.forEach(function (k) {
      if (r.subs[k] === null) return;
      var row = document.createElement('div');
      row.className = 'ms-bar';
      row.innerHTML = '<span class="ms-bar__name"></span><span class="ms-bar__num"></span><span class="ms-bar__track"><span class="ms-bar__fill"></span></span>';
      row.querySelector('.ms-bar__name').textContent = cfg.subscoreNames[k];
      row.querySelector('.ms-bar__num').textContent = r.subs[k];
      row.querySelector('.ms-bar__fill').style.width = r.subs[k] + '%';
      bars.appendChild(row);
    });
    var lv = levers(r, ranked);
    setRow('lever1', lv[0] && lv[0].name, lv[0] && lv[0].detail);
    setRow('lever2', lv[1] && lv[1].name, lv[1] && lv[1].detail);
    setRow('lifestage', r.lifestage, '');

    // Safety route: consult hides the projection; the consult card always comes after the product.
    $$('[data-ms-hide-on-gate="consult"]').forEach(function (el) { el.hidden = r.route === 'consult'; });
    var consult = $('[data-ms-consult]');
    var outcome = document.getElementById('ms-outcome');
    consult.hidden = !(r.route === 'consult' || r.route === 'only' || cfg.consultAlways);
    $('[data-ms-consult-intro]').hidden = r.route !== 'consult';
    outcome.parentNode.insertBefore(consult, outcome.nextSibling);

    // R3 + R4
    renderProjection(r);
    renderProducts(r, ranked);
  }

  function renderProjection(r) {
    var start = fromISO(r.start);
    var days = Math.round(num(cfg.projection.days, 90));
    var end = addDays(start, days - 1);
    var target = Math.min(100, r.score + Math.round((100 - r.score) * num(cfg.projection.factor, 0.5)));
    var vars = {
      score: r.score, doelscore: target, dagen: days,
      start: formatDate(start), eind: formatDate(end),
      streefgewicht: r.targetWeight ? String(r.targetWeight).replace('.', ',') : ''
    };
    $$('#ms-projection [data-tpl]').forEach(function (el) { el.textContent = fill(el.getAttribute('data-tpl'), vars); });
    var goalEl = $('[data-ms-goal-copy]');
    goalEl.textContent = ' ' + fill(goalEl.getAttribute(vars.streefgewicht ? 'data-tpl-goal' : 'data-tpl-nogoal'), vars);
    drawProjection(start, end, r.score, target, '', null);
  }

  // Line chart of the SCORE over the routine. Never a weight.
  function drawProjection(start, end, score, target, moment, momentDate) {
    var W = 340, H = 190, L = 30, R = 20, T = 30, B = 30;
    var totalDays = daysBetween(start, end);
    var last = momentDate && momentDate > end && daysBetween(end, momentDate) <= 45 ? momentDate : end;
    var span = daysBetween(start, last) || 1;
    function x(d) { return L + (daysBetween(start, d) / span) * (W - L - R); }
    function y(v) { return T + (1 - v / 100) * (H - T - B); }

    var pts = [];
    for (var i = 0; i <= 12; i++) {
      var t = i / 12;
      var eased = 1 - Math.pow(1 - t, 2); // quick start, then steadier
      pts.push([x(addDays(start, Math.round(totalDays * t))), y(score + (target - score) * eased)]);
    }
    var path = pts.map(function (p, i) { return (i ? 'L' : 'M') + p[0].toFixed(1) + ' ' + p[1].toFixed(1); }).join(' ');
    var area = path + ' L' + pts[pts.length - 1][0].toFixed(1) + ' ' + y(0) + ' L' + L + ' ' + y(0) + ' Z';

    var svg = '<svg viewBox="0 0 ' + W + ' ' + H + '" role="img" aria-label="' + score + ' → ' + target + '">';
    [0, 50, 100].forEach(function (v) {
      svg += '<line x1="' + L + '" x2="' + (W - R) + '" y1="' + y(v) + '" y2="' + y(v) + '" class="ms-grid"/>';
      svg += '<text x="' + (L - 8) + '" y="' + (y(v) + 3) + '" text-anchor="end" class="ms-chart__axis">' + v + '</text>';
    });
    // Month ticks. Skip a label that would sit too close to the previous one
    // (e.g. a routine starting on 27 September would print "sep" and "okt" on top of each other).
    var m = new Date(start.getFullYear(), start.getMonth(), 1);
    var lastLabelX = -999;
    while (m <= last) {
      var d = m < start ? start : m;
      var tx = x(d);
      if (tx - lastLabelX >= 34) {
        svg += '<text x="' + tx.toFixed(1) + '" y="' + (H - 8) + '" text-anchor="middle" class="ms-chart__axis">' +
          d.toLocaleDateString(locale, { month: 'short' }).replace('.', '') + '</text>';
        lastLabelX = tx;
      }
      m = new Date(m.getFullYear(), m.getMonth() + 1, 1);
    }
    svg += '<path d="' + area + '" class="ms-area"/>';
    svg += '<path d="' + path + '" class="ms-line"/>';
    svg += '<circle cx="' + pts[0][0] + '" cy="' + pts[0][1] + '" r="4.5" class="ms-point"/>';
    // "47 vandaag" sits below the starting point, where the rising line never goes.
    svg += '<text x="' + (pts[0][0] + 8) + '" y="' + (pts[0][1] + 18) + '" class="ms-chart__label ms-chart__label--strong ms-chart__label--halo">' + score + ' ' + escapeHTML(cfg.projection.todayLabel || '') + '</text>';
    var e = pts[pts.length - 1];
    svg += '<circle cx="' + e[0] + '" cy="' + e[1] + '" r="4.5" class="ms-point"/>';
    svg += '<text x="' + (e[0] - 6) + '" y="' + (e[1] - 10) + '" text-anchor="end" class="ms-chart__label ms-chart__label--strong ms-chart__label--halo">' + target + ' · ' + escapeHTML(formatShort(end)) + '</text>';
    if (moment && momentDate && momentDate >= start && momentDate <= last) {
      var mx = x(momentDate);
      svg += '<line x1="' + mx + '" x2="' + mx + '" y1="' + (T - 12) + '" y2="' + y(0) + '" class="ms-marker"/>';
      var label = moment.charAt(0).toUpperCase() + moment.slice(1) + ', ' + formatShort(momentDate);
      svg += '<text x="' + Math.min(mx, W - 4) + '" y="' + (T - 16) + '" text-anchor="end" class="ms-chart__label">' + escapeHTML(label) + '</text>';
    }
    svg += '</svg>';
    $('[data-ms-projection-chart]').innerHTML = svg;
  }

  function escapeHTML(s) {
    return String(s).replace(/[&<>"']/g, function (c) { return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]; });
  }

  // Turns "gratis consult" / "gratis consultatie" in a text into a link to the consult booking page
  // (or to the consult card on the page when no booking link is set). Built with DOM nodes, no HTML strings.
  function linkConsult(el) {
    var text = el.textContent;
    var m = /gratis consult(atie)?/i.exec(text);
    if (!m) return;
    var a = document.createElement('a');
    a.className = 'ms-inline-link';
    a.textContent = m[0];
    if (cfg.consultUrl) { a.href = cfg.consultUrl; a.target = '_blank'; a.rel = 'noopener'; }
    else { a.href = '#'; a.addEventListener('click', function (e) { e.preventDefault(); var c = $('[data-ms-consult]'); if (c) c.scrollIntoView({ behavior: 'smooth' }); }); }
    a.addEventListener('click', function () { track('quiz_consult_click', { from: 'reason' }); });
    el.textContent = '';
    el.appendChild(document.createTextNode(text.slice(0, m.index)));
    el.appendChild(a);
    el.appendChild(document.createTextNode(text.slice(m.index + m[0].length)));
  }

  function renderProducts(r, ranked) {
    var slot = $('[data-ms-main-slot]');
    var singleSlot = $('[data-ms-single-slot]');
    productOrder.forEach(function (k) {
      var p = products[k];
      p.el.hidden = k !== r.main;
      if (p.addonEl) p.addonEl.hidden = k !== r.single;
    });
    var p = products[r.main];
    if (p && p.el.parentNode !== slot) slot.appendChild(p.el);

    // The products inside the main pack, to buy separately (one-time purchase).
    var looseBox = $('[data-ms-loose]');
    if (looseBox) {
      var looseList = $('[data-ms-loose-list]', looseBox);
      var inside = p ? p.contents.filter(function (k) { return products[k] && products[k].looseEl; }) : [];
      productOrder.forEach(function (k) { if (products[k].looseEl) products[k].looseEl.hidden = true; });
      inside.forEach(function (k) { var el = products[k].looseEl; looseList.appendChild(el); el.hidden = false; });
      looseBox.hidden = !inside.length;
    }

    // Second card: the extra suggestion, or one single product matched to the biggest lever.
    var single = r.single && products[r.single];
    var singleBox = $('[data-ms-single]');
    singleBox.hidden = !(single && single.addonEl);
    if (single && single.addonEl) {
      singleSlot.appendChild(single.addonEl);
      var extra = secondCardIsExtra(r.route);
      var titleEl = $('[data-ms-single-title]');
      titleEl.textContent = titleEl.getAttribute(extra ? 'data-extra' : 'data-single') || '';
      var textEl = $('[data-ms-single-text]');
      var lever = r.singleLever ? cfg.subscoreNames[r.singleLever] : '';
      var text = extra ? (textEl.getAttribute('data-tpl-extra') || '') : (lever ? fill(textEl.getAttribute('data-tpl'), { hefboom: lever }) : '');
      textEl.textContent = text;
      textEl.hidden = !text;
    }

    var actions = $('[data-ms-actions]');
    actions.innerHTML = '';
    if (!p) return;
    var reasonEl = $('[data-ms-reason]', p.el);
    if (r.route !== 'none' && r.gateReason) {
      reasonEl.textContent = r.gateReason;
      if (r.route === 'consult' || r.route === 'only') linkConsult(reasonEl);
    } else if (r.route === 'consult' || r.route === 'only') {
      reasonEl.textContent = cfg.gateReasonGeneric;
    } else {
      reasonEl.textContent = fill(reasonEl.getAttribute('data-tpl'), {
        laagste: cfg.subscoreNames[ranked[0]] || '',
        tweede: cfg.subscoreNames[ranked[1]] || '',
        doel: r.goalSummary || ''
      });
    }
    // Routine actions as plain bullet points.
    lines(p.actions).forEach(function (text) {
      var li = document.createElement('li');
      li.textContent = text;
      actions.appendChild(li);
    });
  }

  /* ─────────────── 10. Result link (for the email) ─────────────── */
  // The link holds only what the result page shows: scores, products, route and
  // habit answers. No health answers, no height or weight, no email.

  function encodeResult(r) {
    var data = { v: 2, s: [r.score, r.subs.sugar, r.subs.fat, r.subs.rest, r.subs.basis], o: r.main, x: r.single, l: r.singleLever, g: r.route, t: r.start, a: r.answers };
    var b64 = btoa(unescape(encodeURIComponent(JSON.stringify(data))));
    return b64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function decodeResult(str) {
    try {
      var b64 = str.replace(/-/g, '+').replace(/_/g, '/');
      var data = JSON.parse(decodeURIComponent(escape(atob(b64))));
      if (data.v !== 2) return null;
      var r = {
        score: data.s[0], subs: { sugar: data.s[1], fat: data.s[2], rest: data.s[3], basis: data.s[4] },
        main: data.o, single: Array.isArray(data.x) ? data.x[0] : (data.x || null), singleLever: data.l || null, route: data.g || 'none', start: data.t,
        targetWeight: null, gateReason: '', answers: data.a || {}, lifestage: ''
      };
      var gq = byRole('goal');
      var ga = gq && r.answers[gq.key];
      r.goalSummary = typeof ga === 'number' && gq.options[ga] ? gq.options[ga].summary : '';
      return r;
    } catch (e) { warn('Result link could not be read.'); return null; }
  }
  function resultUrl(r) {
    var base = window.location.href.split('#')[0].split('?')[0];
    return base + '?r=' + encodeResult(r) + '#resultaat';
  }

  /* ─────────────────────────── 11. Klaviyo ─────────────────────────── */

  function klaviyoPush(args) {
    try {
      if (window.klaviyo && typeof window.klaviyo.push === 'function') { window.klaviyo.push(args); return true; }
      if (window._learnq && typeof window._learnq.push === 'function') { window._learnq.push(args); return true; }
    } catch (e) { warn('Klaviyo not available.'); }
    return false;
  }

  function resultProperties(r) {
    var main = products[r.main];
    return {
      MetabolismeScore: r.score,
      ScoreSuikerCravings: r.subs.sugar,
      ScoreVetVocht: r.subs.fat,
      ScoreRustHerstel: r.subs.rest,
      ScoreBasis: r.subs.basis,
      ScoreBand: cfg.bands[band(r.score)].name,
      Uitkomst: r.main || '',
      UitkomstNaam: main ? main.title : '',
      LosProduct: r.single && products[r.single] ? products[r.single].title : '',
      Route: r.route,                  // none / consult / only / pick. Never the reason.
      RoutineStart: r.start,
      RoutineEinde: toISO(addDays(fromISO(r.start), Math.round(num(cfg.projection.days, 90)) - 1)),
      ResultaatUrl: resultUrl(r),
      Taal: cfg.language
    };
  }

  // For the emails: what the score means and the products shown, each with price, photo, link, stars and two
  // benefit lines (Klaviyo template: {% for p in event.products %} … {{ p.title }}, {{ p.claim_1 }} …).
  // On the event only, not on the profile. No reason on a safety route: even the general text hints at health data.
  function stars(rating) {
    var full = Math.max(0, Math.min(5, Math.round(rating)));
    return new Array(full + 1).join('★') + new Array(6 - full).join('☆');
  }

  function emailProduct(key, role) {
    var p = products[key];
    if (!p) return null;
    var e = p.email || {};
    var lines = function (t) { return String(t || '').split('\n').map(function (l) { return l.trim(); }).filter(Boolean); };
    var claims = lines(e.claims);
    if (!claims.length) claims = lines(e.roles);
    if (!claims.length) claims = lines(e.addon_line);
    var rating = typeof e.rating === 'number' ? e.rating : null;
    var count = e.rating_count || null;
    return {
      role: role,                      // main / extra
      key: key,
      title: e.title || p.title || '',
      url: e.url || '',
      image: e.image || '',
      price: e.price || '',
      compare_price: e.compare_price || '',
      subscription_price: e.subscription_price || '',
      rating: rating,
      rating_count: count,
      stars: rating === null ? '' : stars(rating),
      rating_display: rating === null ? '' : String(rating).replace('.', ',') + (count ? ' – ' + count + ' ' + (e.reviews_label || '') : '').replace(/\s+$/, ''),
      claim_1: claims[0] || '',
      claim_2: claims[1] || ''
    };
  }

  function emailProperties(r) {
    var b = band(r.score);
    var ranked = rankSubs(r.subs);
    var main = products[r.main];
    var reason = '';
    if (r.route === 'none' && main && main.el) {
      var reasonEl = main.el.querySelector('[data-ms-reason]');
      reason = fill(reasonEl ? reasonEl.getAttribute('data-tpl') : '', {
        laagste: cfg.subscoreNames[ranked[0]] || '',
        tweede: cfg.subscoreNames[ranked[1]] || '',
        doel: r.goalSummary || ''
      });
    }
    var list = [emailProduct(r.main, 'main'), r.single !== r.main ? emailProduct(r.single, 'extra') : null].filter(Boolean);
    return {
      ScoreUitleg: (cfg.bandTexts || [])[b] || '',
      GrootsteUitdaging: cfg.subscoreNames[ranked[0]] || '',
      TweedeUitdaging: cfg.subscoreNames[ranked[1]] || '',
      UitkomstUitleg: reason,
      products: list
    };
  }

  // The answers, under the "Klaviyo field name" set per question in the editor (empty: not sent).
  // Single choice: the answer text; several answers: a list of texts.
  // Never the safety question (medication), life stage (pregnancy, menopause, PCOS: health data), height, weight
  // or number/date fields, whatever the editor says.
  var NEVER_TO_KLAVIYO = ['gate', 'lifestage', 'height', 'current_weight'];
  function answerProperties(answers) {
    var out = {};
    questionList.forEach(function (q) {
      var field = String(q.klaviyoField || '').trim();
      if (!field || NEVER_TO_KLAVIYO.indexOf(q.role) !== -1) return;
      if (q.type !== 'single' && q.type !== 'multi') return;
      var a = answers[q.key];
      var idx = typeof a === 'number' ? [a] : (Array.isArray(a) ? a : []);
      var labels = idx.map(function (i) { return q.options[i] && q.options[i].label; }).filter(Boolean);
      if (!labels.length) return;
      out[field] = q.type === 'multi' ? labels : labels[0];
    });
    return out;
  }

  function sendToKlaviyo(email, optedIn, r) {
    var k = cfg.klaviyo;
    if (EDITOR) { log('Theme editor: nothing sent to Klaviyo.'); return; }
    if (!k.enabled) { log('Klaviyo is switched off.'); return; }
    var props = resultProperties(r);
    // Answers only for people who ticked the newsletter box; everyone gets their score and products.
    if (optedIn) Object.assign(props, answerProperties(r.allAnswers || state.answers));

    var eventProps = Object.assign({}, props, emailProperties(r));

    klaviyoPush(['identify', Object.assign({ $email: email }, optedIn ? props : {})]);
    var sent = klaviyoPush(['track', k.eventName || 'Metabolisme Score completed', eventProps]);
    log('Klaviyo event ' + (sent ? 'sent' : 'NOT sent (no Klaviyo script on the page)') + ':', eventProps);

    if (!optedIn) { log('No opt-in: not added to a list.'); return; }
    var listId = cfg.language === 'fr' ? k.listFr : cfg.language === 'en' ? k.listEn : k.listNl;
    if (!k.companyId || !listId) { warn('Opt-in given, but no Klaviyo public key or list ID is set for "' + cfg.language + '".'); return; }

    var body = {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: k.source || 'Metabolisme Score quiz',
          profile: { data: { type: 'profile', attributes: {
            email: email,
            properties: props,
            subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
          } } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    };
    fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(k.companyId), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', revision: '2026-07-15' },
      body: JSON.stringify(body)
    }).then(function (res) {
      log('Klaviyo subscription → HTTP ' + res.status);
    }).catch(function () { warn('Klaviyo subscription failed.'); });
  }

  /* ─────────────────────────── 12. Tracking ─────────────────────────── */
  // Goes to dataLayer, gtag and Shopify's customer events.
  // Never answers, weights or email addresses.

  function track(name, params) {
    if (EDITOR) return;
    var data = Object.assign({ quiz: 'metabolisme_score' }, params);
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push(Object.assign({ event: name }, data));
      if (typeof window.gtag === 'function') window.gtag('event', name, data);
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data);
      }
    } catch (e) { /* tracking must never break the quiz */ }
    log('Track:', name, data);
  }

  /* ─────────────────────────── 13. Cart ─────────────────────────── */
  // Same approach as the theme's own product form: add, swap the drawer HTML,
  // then fire ADD_TO_CART on the theme's event bus so the drawer opens.
  // The theme already sends add_to_cart to the dataLayer for every /cart/add.js call.

  function addToCart(btn) {
    var variant = parseInt(btn.getAttribute('data-variant'), 10);
    var plan = btn.getAttribute('data-plan');
    var card = btn.closest('.ms-outcome, .ms-addon');
    var errorEl = card && $('[data-ms-cart-error]', card);
    if (!variant) return;

    var item = { id: variant, quantity: 1, properties: { _source: 'metabolisme-score' } };
    if (plan) item.selling_plan = parseInt(plan, 10);
    var drawer = document.getElementById('cart-drawer');

    btn.disabled = true;
    btn.classList.add('is-loading');
    if (errorEl) { errorEl.textContent = ''; errorEl.classList.remove('ms-error--visible'); }
    log('Add to cart:', item);

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [item], sections: drawer ? 'cart-drawer,cart-icon-bubble' : undefined })
    }).then(function (res) {
      return res.json().then(function (json) { if (!res.ok) throw new Error(json.description || res.status); return json; });
    }).then(function (json) {
      if (!drawer || !window.WhatSub || !json.sections) { window.location.href = cfg.cartUrl || '/cart'; return; }
      var html = new DOMParser().parseFromString(json.sections['cart-drawer'], 'text/html');
      var fresh = html.querySelector('#cart-drawer');
      if (fresh) { drawer.innerHTML = fresh.innerHTML; drawer.classList.remove('is-empty'); }
      var bubble = document.getElementById('cart-icon-bubble');
      if (bubble && json.sections['cart-icon-bubble']) {
        var b = new DOMParser().parseFromString(json.sections['cart-icon-bubble'], 'text/html').querySelector('.shopify-section');
        if (b) bubble.innerHTML = b.innerHTML;
      }
      window.WhatSub.dispatch('ADD_TO_CART');
      window.WhatSub.dispatch('ADD_TO_CART_REINIT_UPSELL');
    }).catch(function (err) {
      warn('Add to cart failed: ' + err.message);
      if (errorEl) { errorEl.textContent = cfg.errorCart; errorEl.classList.add('ms-error--visible'); }
    }).then(function () {
      btn.disabled = false;
      btn.classList.remove('is-loading');
    });
  }

  /* ─────────────────────────── 14. Start ─────────────────────────── */

  // The email step listens to the button click and to Enter in the field, not only to the
  // form's submit event: some embeds (sandboxed frames, previews) block form submission,
  // and then the submit event never fires.
  var form = $('[data-ms-email-form]');
  if (form) {
    form.addEventListener('submit', function (e) { e.preventDefault(); submitEmail(); });
    form.querySelector('button[type="submit"]').addEventListener('click', function (e) { e.preventDefault(); submitEmail(); });
    form.querySelector('input[type="email"]').addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitEmail(); }
    });
  }

  function submitEmail() {
      if (!form || current.type !== 'email') return;
      var input = form.querySelector('input[type="email"]');
      var email = input.value.trim();
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) {
        setError(form, cfg.errorEmail);
        input.focus();
        return;
      }
      setError(form, '');
      var optedIn = form.querySelector('[data-ms-optin]').checked;

      // Step 1: calculate the result. Without it there is nothing to show.
      try {
        state.result = buildResult();
        save();
      } catch (err) {
        console.error('[MS-Quiz] Could not calculate the result:', err);
        setError(form, cfg.errorResult || cfg.errorCart);
        return;
      }

      // Step 2: Klaviyo and tracking. These may fail (blocked scripts, ad blockers);
      // the visitor must still see the result.
      try { sendToKlaviyo(email, optedIn, state.result); } catch (err) { console.error('[MS-Quiz] Klaviyo step failed:', err); }
      track('quiz_lead', { opted_in: optedIn });
      track('quiz_result', { band: cfg.bands[band(state.result.score)].name, score: state.result.score, outcome: state.result.main || 'consult', single: state.result.single || '', route: state.result.route });
      input.value = '';

      // Step 3: show the result page.
      try {
        show('resultaat');
      } catch (err) {
        console.error('[MS-Quiz] Could not show the result page:', err);
        setError(form, cfg.errorResult || cfg.errorCart);
      }
  }

  runChecks();

  // Debug helper (only with ?debug=1). In the browser console:
  //   MSQuiz.restart()                        start over
  //   MSQuiz.config.singleMap.sugar = 'detox' try another single product, then restart
  //   MSQuiz.state()                          see the current answers and result
  if (DEBUG) {
    window.MSQuiz = {
      config: cfg,
      state: function () { return JSON.parse(JSON.stringify(state)); },
      restart: function () {
        state = { answers: {}, visited: ['start'], result: null };
        save();
        $$('.ms-screen input').forEach(function (i) { if (i.type === 'radio' || i.type === 'checkbox') i.checked = false; else i.value = ''; });
        show('start', { push: false });
        setUrl('replace', 'start');
      }
    };
    log('Debug helper ready: MSQuiz.restart(), MSQuiz.config, MSQuiz.state()');
  }

  /* ───────────── Theme editor: show the screen of the block you click, preview the result page ───────────── */

  // Example answers: per question the first answer without a safety route, so the preview is a normal result.
  function exampleAnswers() {
    var answers = {};
    questionList.forEach(function (q) {
      if (q.type === 'single' || q.type === 'multi') {
        var o = q.options.filter(function (x) { return x.used && x.route === 'none'; })[0];
        if (o) answers[q.key] = q.type === 'multi' ? [o.index] : o.index;
      } else if (q.type === 'number') {
        answers[q.key] = q.role === 'height' ? '168' : q.role === 'current_weight' ? '72' : String(q.min || 1);
      } else if (q.type === 'date') {
        answers[q.key] = toISO(addDays(new Date(), 30));
      }
    });
    return answers;
  }

  // The result page with example answers; mainKey shows that product as the recommendation.
  function previewResult(mainKey) {
    state = { answers: exampleAnswers(), visited: screens.map(function (s) { return s.id; }), result: null };
    state.result = buildResult();
    if (mainKey && products[mainKey]) {
      state.result.main = mainKey;
      if (state.result.single === mainKey) state.result.single = null;
    }
    show('resultaat', { push: false, focus: false });
  }

  function blockIdOf(el) {
    try { return JSON.parse(el.getAttribute('data-shopify-editor-block')).id; } catch (e) { return null; }
  }

  if (EDITOR) {
    document.addEventListener('shopify:block:select', function (e) {
      if (!root.isConnected || !e.detail) return;
      var el = $$('[data-shopify-editor-block]').filter(function (x) { return blockIdOf(x) === e.detail.blockId; })[0];
      if (!el) return;
      var productKey = el.getAttribute('data-ms-product-card');
      if (productKey) { previewResult(productKey); return; }
      var screen = el.closest('.ms-screen');
      if (screen) show(screen.getAttribute('data-screen-id'), { push: false, focus: false });
    });
    if (cfg.editorPreview === 'result') previewResult();
    else show('start', { push: false, focus: false });
    log('Theme editor: preview "' + cfg.editorPreview + '".');
    return;
  }

  var linkParam = new URLSearchParams(window.location.search).get('r');
  var fromLink = linkParam && decodeResult(linkParam);
  if (fromLink) {
    // Opened from the email: show the stored result, nothing else.
    state = { answers: fromLink.answers, visited: ['resultaat'], result: fromLink, fromLink: true };
    log('Result opened from link:', fromLink);
    show('resultaat', { push: false, focus: false });
    setUrl('replace', 'resultaat');
  } else {
    load();
    var hash = window.location.hash.replace('#', '');
    var start = state.visited.indexOf(hash) !== -1 ? hash : 'start';
    if (start === 'resultaat' && !state.result) start = 'start';
    show(start, { push: false, focus: false });
    setUrl('replace', start);
  }
  } // end of boot()

  boot();
  // Theme editor: after a change the section is re-rendered; start the quiz again on the new markup.
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target && e.target.querySelector && e.target.querySelector('#ms-quiz')) boot();
  });
})();
